import React, { useCallback, useContext, useMemo, useRef } from "react";
import {
    BankStatementTransaction,
    FinancialDocumentExtended,
    ImporterErrors,
} from "@common/types/financialDocument";
import { SpreadsheetValue } from "@common/helpers/spreadsheet";
import {
    getFinancialAccountType,
    ImporterSpreadsheetColumns,
} from "@common/helpers/financialDocument";
import { validateTransactionsAgainstBalances } from "@common/helpers/bankStatementExtraction";
import { useRemoveImporterErrorMutation } from "@api/financialDocument.api";
import { PersistentSpreadsheet } from "../spreadsheet/PersistentSpreadsheet";
import styles from "./ImporterPage.module.scss";
import {
    getInvalidTransactionsSummary,
    isTransactionsProcessingFinished,
} from "./helpers";
import { ImporterContext } from "./importer.context";
import { ImporterDataPreviewContent } from "./ImporterDataPreviewContent";
import { ImporterPreviewCommandBar } from "./ImporterPreviewCommandBar";

interface Props {
    financialDocument: FinancialDocumentExtended;
}

export const ImporterDataPreview: React.FC<Props> = ({ financialDocument }) => {
    const { revalidateDocument, setPreviewedRowId } =
        useContext(ImporterContext);
    const reviewed = useRef<number[]>([]);

    const closePreview = useCallback(() => {
        setPreviewedRowId(undefined);
    }, [setPreviewedRowId]);

    const removeImporterErrorMutation =
        useRemoveImporterErrorMutation(financialDocument);

    const onTransactionsUpdate = useCallback(async () => {
        await revalidateDocument({
            financialDocumentId: financialDocument.id,
            checkTransactions: true,
        });
    }, [financialDocument.id, revalidateDocument]);

    const flipSigns = useCallback(
        (
            data: Array<Record<string, SpreadsheetValue>>,
            afterChange: (
                data: Array<Record<string, SpreadsheetValue>>,
            ) => void,
        ) => {
            const newData = data.map((row) => ({
                ...row,
                [ImporterSpreadsheetColumns.AMOUNT]:
                    typeof row[ImporterSpreadsheetColumns.AMOUNT] === "number"
                        ? -row[ImporterSpreadsheetColumns.AMOUNT]
                        : row[ImporterSpreadsheetColumns.AMOUNT],
            }));
            afterChange(newData);
        },
        [],
    );

    const computeDiscrepancy = useCallback(
        (data: Array<Record<string, SpreadsheetValue>>): number | undefined => {
            const transactions: BankStatementTransaction[] = data
                .map((row) => row[ImporterSpreadsheetColumns.AMOUNT])
                .filter((v): v is number => typeof v === "number")
                .map((amount) => ({
                    date: null,
                    description: "",
                    amount,
                }));

            const { discrepancyAmount } = validateTransactionsAgainstBalances({
                transactions,
                openingBalance: financialDocument.openingBalance,
                closingBalance: financialDocument.closingBalance,
                accountType: getFinancialAccountType(financialDocument),
            });

            return discrepancyAmount ?? undefined;
        },
        [financialDocument],
    );

    if (!isTransactionsProcessingFinished(financialDocument)) {
        return (
            <>
                <p className={styles.transactionsMessage}>
                    We're extracting data from this document. Please wait...
                </p>
                <ImporterPreviewCommandBar
                    financialDocument={financialDocument}
                    onClose={closePreview}
                    isExtracting
                />
            </>
        );
    }

    if (
        financialDocument.importerErrors?.includes(
            ImporterErrors.NOT_CONFIDENT,
        ) &&
        !reviewed.current.includes(financialDocument.id)
    ) {
        reviewed.current.push(financialDocument.id);
        removeImporterErrorMutation.mutate(ImporterErrors.NOT_CONFIDENT);
    }

    if (!financialDocument.transactionsSpreadsheetId) {
        return (
            <ImporterPreviewCommandBar
                financialDocument={financialDocument}
                onClose={closePreview}
            />
        );
    }

    return (
        <PersistentSpreadsheet
            spreadsheetId={financialDocument.transactionsSpreadsheetId}
            accessParams={{
                financialDocumentId: financialDocument.id,
            }}
            afterUpdate={onTransactionsUpdate}
        >
            {({ data, afterChange }) => (
                <ImporterDataPreviewInner
                    financialDocument={financialDocument}
                    data={data}
                    afterChange={afterChange}
                    flipSigns={flipSigns}
                    closePreview={closePreview}
                    computeDiscrepancy={computeDiscrepancy}
                />
            )}
        </PersistentSpreadsheet>
    );
};

interface ImporterDataPreviewInnerProps {
    financialDocument: FinancialDocumentExtended;
    data: Array<Record<string, SpreadsheetValue>>;
    afterChange: (data: Array<Record<string, SpreadsheetValue>>) => void;
    flipSigns: (
        data: Array<Record<string, SpreadsheetValue>>,
        afterChange: (data: Array<Record<string, SpreadsheetValue>>) => void,
    ) => void;
    closePreview: () => void;
    computeDiscrepancy: (
        data: Array<Record<string, SpreadsheetValue>>,
    ) => number | undefined;
}

const ImporterDataPreviewInner: React.FC<ImporterDataPreviewInnerProps> = ({
    financialDocument,
    data,
    afterChange,
    flipSigns,
    closePreview,
    computeDiscrepancy,
}) => {
    const hasInvalidTransactionsError =
        financialDocument.importerErrors?.includes(
            ImporterErrors.INVALID_TRANSACTIONS,
        );

    const invalidTransactionsSummary = useMemo(
        () =>
            hasInvalidTransactionsError
                ? getInvalidTransactionsSummary(data)
                : undefined,
        [data, hasInvalidTransactionsError],
    );

    return (
        <>
            <ImporterDataPreviewContent
                financialDocument={financialDocument}
                data={data}
                afterChange={afterChange}
            />
            <ImporterPreviewCommandBar
                financialDocument={financialDocument}
                onFlipSigns={() => flipSigns(data, afterChange)}
                onClose={closePreview}
                discrepancyAmount={computeDiscrepancy(data)}
                invalidTransactionsSummary={invalidTransactionsSummary}
            />
        </>
    );
};
